/* eslint-disable prisma-rule/headings */

import type { FC, HTMLAttributes } from 'react';
import { cn } from 'src/utils/cn';

type BaseProps = HTMLAttributes<HTMLHeadingElement>;

type HeadlineModeProps = { mode: 'headline'; size: 'md' | 'lg' };

type TitleModeProps = { mode: 'title'; size: 'sm' | 'md' };

// Can be deleted after the rollout of the new design, it only supports the legacy design
type FooterModeProps = { mode: 'footer'; size?: never };

export type Props = (HeadlineModeProps | TitleModeProps | FooterModeProps) &
  BaseProps;

const legacyBaseStyles = cn(`
  legacy:font-legacy-heading legacy:font-[900] legacy:text-legacy-brand
`);

const baseHeadlineStyles = cn(`
  not-legacy:font-family-headline not-legacy:font-headline
  not-legacy:text-foreground-default
`);

const baseTitleStyles = cn(`
  not-legacy:font-family-title not-legacy:font-title
  not-legacy:text-foreground-default
`);

export const Heading: FC<Props> = ({ mode, size, ...props }) => {
  switch (mode) {
    case 'footer':
      return (
        <h4
          {...props}
          className={cn(
            legacyBaseStyles,
            `
              legacy:my-[1.05rem] legacy:font-base legacy:text-[1.8rem]
              legacy:leading-[3.2rem] legacy:text-white
            `,
            props.className
          )}
        />
      );

    case 'headline':
      switch (size) {
        case 'lg':
          return (
            <h1
              {...props}
              className={cn(
                legacyBaseStyles,
                `
                  legacy:text-[4.5rem] legacy:leading-[6.75rem]
                  legacy:tracking-[-0.1rem]
                `,
                baseHeadlineStyles,
                `not-legacy:text-headline-lg`,
                props.className
              )}
            />
          );

        case 'md':
          return (
            <h2
              {...props}
              className={cn(
                legacyBaseStyles,
                `
                  legacy:text-[3rem] legacy:leading-[4.5rem]
                  legacy:tracking-[-0.06rem]
                `,
                baseHeadlineStyles,
                `not-legacy:text-headline-md`,
                props.className
              )}
            />
          );
      }

    case 'title':
      switch (size) {
        case 'md':
          return (
            <h3
              {...props}
              className={cn(
                legacyBaseStyles,
                `
                  legacy:text-[2rem] legacy:leading-[3.2rem]
                  legacy:tracking-[-0.04rem]
                `,
                baseTitleStyles,
                `not-legacy:text-title-md`,
                props.className
              )}
            />
          );

        case 'sm':
          return (
            <h4
              {...props}
              className={cn(
                legacyBaseStyles,
                `legacy:font-base legacy:text-[1.8rem] legacy:leading-[3.2rem]`,
                baseTitleStyles,
                `not-legacy:text-title-sm`,
                props.className
              )}
            />
          );
      }
  }
};
